import type { Tile } from "../types";
import type { FaceWithBattens, Line } from "../types/roof";

// Logic: Loops through all roof faces, finds faces with a top ridge,
// subtracts cloak verge widths if present, divides by cover width, and rounds up.
const getMainTileTopClipQuantity = (
  tileVariant: Tile,
  facesWithBattens: FaceWithBattens[],
  roofRidgeLines: Line[]
): number => {
  if (!tileVariant.mainTileTopClip) {
    return 0;
  }

  let absoluteTotalTopClips = 0;

  const leftVergeWidth = tileVariant.vergeOption?.left.coverWidth || 0;
  const rightVergeWidth = tileVariant.vergeOption?.right.coverWidth || 0;

  facesWithBattens.forEach((face, index) => {
    let rawRidgeLength = 0;

    // Find the maximum horizontal width at the top vertex coordinates
    if (face.vertices.length >= 4) {
      const topPoints = face.vertices.filter((v) => v.y > 0);

      if (topPoints.length >= 2) {
        const xCoords = topPoints.map((v) => v.x);
        rawRidgeLength = Math.max(...xCoords) - Math.min(...xCoords);
      }
    }

    // Alternative fallback pattern logic matching global line parameters
    // eslint-disable-next-line security/detect-object-injection
    if (rawRidgeLength === 0 && roofRidgeLines[index]) {
      // eslint-disable-next-line security/detect-object-injection
      rawRidgeLength = roofRidgeLines[index].length;
    } else if (rawRidgeLength === 0 && roofRidgeLines[0]) {
      rawRidgeLength = roofRidgeLines[0].length;
    }

    // Only compute if this face actually interacts with a ridge boundary
    if (rawRidgeLength > 0) {
      // Check if this specific face has cloaked verge trims on its borders
      if (face.sides && face.sides[0] === "VERGE") {
        rawRidgeLength -= leftVergeWidth;
      }
      if (face.sides && face.sides[1] === "VERGE") {
        rawRidgeLength -= rightVergeWidth;
      }

      // Divide the adjusted length by the tile cover width and round up
      if (rawRidgeLength > 0) {
        const faceTopClips = Math.ceil(rawRidgeLength / tileVariant.coverWidth);
        absoluteTotalTopClips += faceTopClips;
      }
    }
  });

  return absoluteTotalTopClips;
};

export default getMainTileTopClipQuantity;
